import { useEffect, useState } from 'react';
import api from '../services/api';
import ImagemProduto from './ImagemProduto';
import { MODO_SEM_FASE_ANALISE } from '../config';

const brl = v => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function dataFmt(d) {
  if (!d) return '—';
  const s = typeof d === 'string' ? d : new Date(d).toISOString();
  const [y, m, day] = s.split('T')[0].split('-');
  return `${day}/${m}/${y}`;
}

const FILIAIS = {
  '01': 'Espera Feliz', '02': 'Caparaó', '03': 'Alto Caparaó',
  '04': 'Central', '05': 'Manhumirim', '06': 'Carangola',
  '07': 'Manhuaçu', '08': 'Carangola Express',
};

/**
 * Modal de detalhe do pedido — abre por click numa venda do Dashboard ou da
 * aba Analítica de Comissões. Mostra:
 *   1. Cabeçalho do pedido (cliente, filial, data)
 *   2. Lista de itens com foto + flag paga/não paga + comissão item
 *   3. Cálculo passo a passo (transparência)
 *   4. Status do CREIND (data emissão, dias, pago/não)
 *
 * Props:
 *   - numped: número do pedido (obrigatório)
 *   - aberto: boolean
 *   - aoFechar: callback
 */
export default function ModalDetalhePedido({ numped, aberto, aoFechar }) {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!aberto || !numped) { setDados(null); return; }
    setCarregando(true);
    setErro('');
    api.get(`/comissoes/detalhe-pedido/${numped}`)
      .then(r => setDados(r.data))
      .catch(e => setErro(e.response?.data?.error || 'Erro ao carregar detalhe do pedido'))
      .finally(() => setCarregando(false));
  }, [numped, aberto]);

  // ESC pra fechar
  useEffect(() => {
    if (!aberto) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') aoFechar?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [aberto, aoFechar]);

  // Trava scroll do body
  useEffect(() => {
    if (!aberto) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [aberto]);

  if (!aberto) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h5 className="modal-title fw-bold mb-1">
                  Pedido <span style={{ color: 'var(--primary)' }}>#{numped}</span>
                </h5>
                {dados?.pedido && (
                  <div className="text-muted" style={{ fontSize: '0.88rem' }}>
                    {dataFmt(dados.pedido.dtpedido)}
                    {dados.pedido.filial && ` · Filial ${FILIAIS[dados.pedido.filial] || dados.pedido.filial}`}
                    {dados.pedido.codvend && ` · Vendedor ${dados.pedido.codvend}`}
                  </div>
                )}
              </div>
              <button type="button" className="btn-close" aria-label="Fechar" onClick={aoFechar} />
            </div>

            <div className="modal-body">
              {carregando && (
                <div className="text-center py-5">
                  <div className="spinner-border" />
                </div>
              )}
              {erro && <div className="alert alert-danger">{erro}</div>}

              {dados && (
                <>
                  {/* Cliente */}
                  {dados.pedido.razaocli && (
                    <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
                      <i className="bi bi-person-vcard" style={{ color: 'var(--primary)' }}></i>
                      <span className="text-muted" style={{ fontSize: '0.85rem' }}>Cliente:</span>
                      <span className="fw-semibold">{dados.pedido.razaocli}</span>
                    </div>
                  )}

                  {/* Lista de itens */}
                  <div className="tabela-container mb-3">
                    <table className="table table-hover table-arq mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: 80 }}>Foto</th>
                          <th>Produto</th>
                          <th className="text-end">Qtd</th>
                          <th className="text-end">Preço</th>
                          <th className="text-end">Linha</th>
                          <th className="text-end" style={{ minWidth: 120 }}>Sua comissão</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dados.itens.map((it, i) => (
                          <tr key={`${it.codpro}-${i}`}>
                            <td>
                              <ImagemProduto
                                codpro={it.codpro}
                                tamanho="md"
                                clicavel
                                alt={it.descr}
                              />
                            </td>
                            <td>
                              <div className="fw-semibold" style={{ fontSize: '0.92rem', lineHeight: 1.35 }}>
                                {it.descr || it.codpro}
                              </div>
                              <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                Cód {it.codpro}
                                {it.clasprod && ` · Classe ${it.clasprod}`}
                              </div>
                            </td>
                            <td className="text-end text-muted" style={{ whiteSpace: 'nowrap' }}>
                              {it.quant.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}
                            </td>
                            <td className="text-end text-muted" style={{ whiteSpace: 'nowrap' }}>
                              {brl(it.preco)}
                            </td>
                            <td className="text-end fw-semibold" style={{ whiteSpace: 'nowrap' }}>
                              {brl(it.linha)}
                            </td>
                            <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                              {it.paga_comissao ? (
                                <span style={{ color: 'var(--primary)', fontWeight: 700 }}>
                                  {brl(it.comissao_item)}
                                </span>
                              ) : (
                                <span className="badge-neutro">não paga</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Cálculo passo a passo */}
                  <div className="card p-3" style={{ background: 'var(--primary-light)', border: 'none' }}>
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className="bi bi-calculator-fill" style={{ color: 'var(--primary)', fontSize: '1.2rem' }}></i>
                      <h6 className="fw-bold mb-0" style={{ color: 'var(--primary-dark)' }}>Como sua comissão é calculada</h6>
                    </div>

                    <div className="row g-2" style={{ fontSize: '0.92rem' }}>
                      <PassoCalculo
                        rotulo="Bruto do pedido"
                        valor={brl(dados.totais.bruto)}
                        descricao={`Soma de todos os ${dados.itens.length} itens`}
                      />
                      <PassoCalculo
                        rotulo="Itens que pagam comissão"
                        valor={brl(dados.totais.qualifica)}
                        descricao={`${dados.itens.filter(i => i.paga_comissao).length} de ${dados.itens.length} itens`}
                      />
                      {dados.totais.desconto_pedido > 0 && (
                        <PassoCalculo
                          rotulo="Desconto do pedido (rateio proporcional)"
                          valor={`- ${brl(dados.totais.desconto_pedido * dados.totais.qualifica / Math.max(dados.totais.bruto, 1))}`}
                          descricao={`O desconto total de ${brl(dados.totais.desconto_pedido)} é dividido proporcionalmente entre os itens`}
                          negativo
                        />
                      )}
                      <PassoCalculo
                        rotulo="Base para comissão"
                        valor={brl(dados.totais.rateio)}
                        destaque
                      />
                      <PassoCalculo
                        rotulo={`Sua comissão (${dados.totais.percentual_arquiteto}%)`}
                        valor={brl(dados.totais.comissao_calculada)}
                        primario
                      />
                    </div>
                  </div>

                  {/* Status do CREIND */}
                  {dados.creind && (
                    <div className="mt-3 d-flex flex-wrap gap-2 align-items-center">
                      <span className="text-muted" style={{ fontSize: '0.88rem' }}>
                        Comissão emitida em <strong>{dataFmt(dados.creind.emissao)}</strong> · há <strong>{dados.creind.dias_emissao}</strong> dias
                      </span>
                      {dados.creind.pagamento ? (
                        <span className="badge-positivo">Já recebido em {dataFmt(dados.creind.pagamento)}</span>
                      ) : (MODO_SEM_FASE_ANALISE || dados.creind.dias_emissao > 30) ? (
                        <span className="badge-filial">Disponível para saque</span>
                      ) : (
                        <span className="badge-pendente">Em análise · libera em {30 - dados.creind.dias_emissao} dias</span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-outline-arq" onClick={aoFechar}>Fechar</button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={aoFechar} />
    </>
  );
}

function PassoCalculo({ rotulo, valor, descricao, destaque, primario, negativo }) {
  return (
    <div className="col-12 d-flex justify-content-between align-items-center" style={{
      padding: '8px 4px',
      borderBottom: destaque || primario ? 'none' : '1px solid rgba(46,152,70,0.15)',
      borderTop: primario ? '2px solid var(--primary)' : 'none',
      marginTop: primario ? 8 : 0,
      paddingTop: primario ? 12 : 8,
    }}>
      <div>
        <div style={{
          fontWeight: primario ? 700 : (destaque ? 600 : 500),
          color: primario ? 'var(--primary-dark)' : (destaque ? 'var(--text)' : 'var(--text-suave)'),
          fontSize: primario ? '0.98rem' : '0.92rem',
        }}>{rotulo}</div>
        {descricao && (
          <div className="text-muted" style={{ fontSize: '0.78rem' }}>{descricao}</div>
        )}
      </div>
      <div style={{
        fontWeight: primario ? 800 : (destaque ? 700 : 600),
        color: primario ? 'var(--primary)' : (negativo ? '#B53D3D' : (destaque ? 'var(--text)' : 'var(--text-suave)')),
        fontSize: primario ? '1.25rem' : (destaque ? '1rem' : '0.95rem'),
        whiteSpace: 'nowrap',
      }}>{valor}</div>
    </div>
  );
}
